/**
 * FILE-OVERVIEW:
 * - 役割: フロントエンドの運用ログを S3 へ構造化JSONで保存する。
 * - 主な連携: index.js, App.js, services/calendarApi.js
 * - 本番運用上の注意: 機微情報（password/token等）は details に渡さないこと。
 */

import { S3Client, PutObjectCommand, GetObjectCommand } from '@aws-sdk/client-s3';
import { fetchAuthSession } from 'aws-amplify/auth';
import awsExports from '../aws-exports';

const LOG_BUCKET_NAME = process.env.REACT_APP_S3_LOG_BUCKET_NAME || 'schedule-memo-log';
const REGION = process.env.REACT_APP_S3_LOG_REGION || process.env.REACT_APP_AWS_REGION || awsExports.aws_project_region || 'ap-northeast-1';
const APP_NAME = 'memo-app-web';
const LOG_FORMAT = (process.env.REACT_APP_APP_LOG_FORMAT || 'line').toLowerCase();

const EVENT_LABELS = {
  auth_startup_check: '認証初期化チェック成功',
  auth_startup_check_failed: '認証初期化チェック失敗',
  auth_module_bootstrap_failed: '認証モジュール起動失敗',
  auth_session_resolved: '認証セッション取得成功',
  auth_session_resolve_failed: '認証セッション取得失敗',
  api_request_failed: 'APIリクエスト失敗',
};

const createDailyLogKey = (ext = 'log') => {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');

  return `web/${yyyy}/${mm}/${dd}/daily-log.${ext}`;
};

const sanitizeDetails = (details) => {
  if (!details || typeof details !== 'object') return details;
  const blockKeys = ['password', 'token', 'authorization', 'accessToken', 'idToken', 'refreshToken'];
  const clone = Array.isArray(details) ? [...details] : { ...details };
  Object.keys(clone).forEach((key) => {
    if (blockKeys.includes(String(key))) {
      clone[key] = '[REDACTED]';
    }
  });
  return clone;
};

const getActor = async () => {
  try {
    const session = await fetchAuthSession();
    const payload = session?.tokens?.idToken?.payload || {};
    return {
      userId: payload.sub || null,
      username: payload['cognito:username'] || payload.email || null,
      groups: payload['cognito:groups'] || [],
    };
  } catch (_) {
    return {
      userId: null,
      username: null,
      groups: [],
    };
  }
};

const getEventLabel = (event) => EVENT_LABELS[event] || `イベント:${event}`;

const getLevelLabel = (level) => {
  switch (String(level || 'INFO').toUpperCase()) {
    case 'ERROR':
      return 'エラー';
    case 'WARN':
      return '警告';
    default:
      return '情報';
  }
};

const safeText = (value) => String(value ?? '-').replace(/[\r\n\t]/g, ' ').trim();

const bodyToString = async (body) => {
  if (!body) return '';
  if (typeof body.transformToString === 'function') {
    return body.transformToString();
  }
  if (typeof body.text === 'function') {
    return body.text();
  }
  if (typeof Response !== 'undefined') {
    try {
      return await new Response(body).text();
    } catch (_) {
      return '';
    }
  }
  return '';
};

const readExistingDailyLog = async (s3Client, key) => {
  try {
    const existing = await s3Client.send(new GetObjectCommand({
      Bucket: LOG_BUCKET_NAME,
      Key: key,
    }));

    return await bodyToString(existing.Body);
  } catch (error) {
    const name = error?.name || '';
    const status = error?.$metadata?.httpStatusCode;
    if (name === 'NoSuchKey' || status === 404) {
      return '';
    }
    if (name === 'AccessDenied' || status === 403) {
      console.error('既存日次ログの取得に失敗(GetObject権限不足の可能性):', {
        bucket: LOG_BUCKET_NAME,
        key,
      });
    }
    throw error;
  }
};

const flattenDetails = (details) => {
  if (details === null || details === undefined) return '';
  if (typeof details !== 'object') return String(details);
  const pairs = Object.entries(details).map(([key, value]) => {
    if (value === null || value === undefined) return `${key}=-`;
    if (Array.isArray(value)) return `${key}=${value.join('|')}`;
    if (typeof value === 'object') return `${key}=${JSON.stringify(value)}`;
    return `${key}=${String(value)}`;
  });
  return pairs.join(' ');
};

const toLogLine = (payload) => {
  const actor = payload.actor || {};
  const groups = Array.isArray(actor.groups) ? actor.groups.join('|') : '';
  const detailsText = safeText(flattenDetails(payload.details));
  const messageText = payload.message ? String(payload.message) : '-';
  const pathText = payload.path || '-';
  const userText = actor.username || actor.userId || 'anonymous';
  const eventLabel = getEventLabel(payload.event);
  const levelLabel = getLevelLabel(payload.level);
  return [
    `時刻=${safeText(payload.timestamp)}`,
    `レベル=${safeText(levelLabel)}`,
    `イベント=${safeText(eventLabel)}`,
    `イベントID=${safeText(payload.event)}`,
    `ユーザー=${safeText(userText)}`,
    `グループ=${safeText(groups || '-')}`,
    `画面=${safeText(pathText)}`,
    `メッセージ=${safeText(messageText)}`,
    `詳細=${detailsText || '-'}`,
  ].join(' ');
};

export const writeAppLog = async ({ level = 'INFO', event, message, details }) => {
  if (!event) return;

  const useJson = LOG_FORMAT === 'json';
  const key = createDailyLogKey(useJson ? 'json' : 'log');

  try {
    const session = await fetchAuthSession();
    if (!session?.credentials) {
      console.error('S3ログ送信失敗: 認証クレデンシャルが取得できません', {
        event,
        bucket: LOG_BUCKET_NAME,
        region: REGION,
      });
      return;
    }

    const s3Client = new S3Client({
      region: REGION,
      credentials: session.credentials,
    });

    const actor = await getActor();
    const payload = {
      timestamp: new Date().toISOString(),
      app: APP_NAME,
      level,
      event,
      message: message || null,
      actor,
      details: sanitizeDetails(details || null),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      path: typeof window !== 'undefined' ? window.location.pathname : null,
    };

    const newEntry = useJson ? `${JSON.stringify(payload)}\n` : `${toLogLine(payload)}\n`;
    const existingBody = await readExistingDailyLog(s3Client, key);
    const body = `${existingBody || ''}${newEntry}`;
    const contentType = useJson ? 'application/json' : 'text/plain; charset=utf-8';

    const command = new PutObjectCommand({
      Bucket: LOG_BUCKET_NAME,
      Key: key,
      Body: body,
      ContentType: contentType,
    });

    await s3Client.send(command);
  } catch (error) {
    // ログ送信失敗で本処理を止めない
    console.error('S3ログ送信失敗:', {
      event,
      bucket: LOG_BUCKET_NAME,
      key,
      region: REGION,
      errorName: error?.name || null,
      errorMessage: error?.message || String(error),
      httpStatusCode: error?.$metadata?.httpStatusCode || null,
    });
  }
};

export const logInfo = (event, details, message = null) => writeAppLog({
  level: 'INFO',
  event,
  details,
  message,
});

export const logWarn = (event, details, message = null) => writeAppLog({
  level: 'WARN',
  event,
  details,
  message,
});

export const logError = (event, details, message = null) => writeAppLog({
  level: 'ERROR',
  event,
  details,
  message,
});
